import { c as _c } from "react/compiler-runtime";
import { IcoPencil16 } from '@onefootprint/icons';
import type { EntityStatusEntryOption } from '@onefootprint/request-types/dashboard';
import { Button, Divider, Form, IconButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ColorComponent from '../color-component';
export type CustomStatusEntry = EntityStatusEntryOption & {
  isEditing: boolean;
};
type CustomStatusEntryProps = {
  entry: CustomStatusEntry;
  showDivider: boolean;
  shouldShowSlug: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onSave: (entryData: CustomStatusEntryFormData) => Promise<void>;
  onCancel: () => void;
};
export type CustomStatusEntryFormData = {
  displayName: string;
  slug: string;
  hexCode: string;
};
const CustomStatusEntryItem = t0 => {
  const $ = _c(26);
  const {
    entry,
    showDivider,
    shouldShowSlug,
    canEdit,
    onSave,
    onEdit,
    onCancel
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "pages.statuses.customStatusCard.entry"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("settings", t1);
  let t2;
  if ($[1] !== entry.displayName || $[2] !== entry.hexCode || $[3] !== entry.slug) {
    t2 = {
      defaultValues: {
        displayName: entry.displayName,
        slug: entry.slug,
        hexCode: entry.hexCode
      }
    };
    $[1] = entry.displayName;
    $[2] = entry.hexCode;
    $[3] = entry.slug;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const form = useForm(t2);
  let t3;
  if ($[5] !== entry.id) {
    t3 = entry.id.startsWith("custom-entry-temp-id-");
    $[5] = entry.id;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const isNew = t3;
  let t4;
  if ($[7] !== form || $[8] !== onCancel) {
    t4 = () => {
      form.reset();
      onCancel();
    };
    $[7] = form;
    $[8] = onCancel;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleCancelEdit = t4;
  let t5;
  if ($[10] !== onSave) {
    t5 = entryData => {
      onSave(entryData);
    };
    $[10] = onSave;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleSave = t5;
  let t6;
  if ($[12] !== canEdit || $[13] !== entry.displayName || $[14] !== entry.hexCode || $[15] !== entry.isEditing || $[16] !== entry.slug || $[17] !== form || $[18] !== handleCancelEdit || $[19] !== handleSave || $[20] !== isNew || $[21] !== onEdit || $[22] !== shouldShowSlug || $[23] !== showDivider || $[24] !== t) {
    t6 = entry.isEditing || isNew ? <FormProvider {...form}><div className="flex flex-col gap-2"><div className="flex flex-col gap-3 px-3 py-2 md:flex-row md:items-center md:justify-between md:gap-0"><div className="grid grid-cols-[auto_minmax(0,1fr)] gap-2 md:flex"><Controller name="hexCode" control={form.control} render={_temp} /><Form.Input name="displayName" size="compact" containerClassName="md:w-[300px]" rules={{
              required: t(_temp2)
            }} placeholder={t(_temp3)} />{isNew && <div className="col-start-2 md:contents"><Form.Input name="slug" size="compact" containerClassName="md:w-[300px]" rules={{
                required: t(_temp4)
              }} placeholder={t(_temp5)} autoCapitalize="none" autoCorrect="off" /></div>}</div><div className="flex justify-end gap-2 md:justify-start"><Button variant="secondary" onClick={handleCancelEdit}>{t(_temp6)}</Button><Button variant="primary" onClick={form.handleSubmit(handleSave)}>{t(_temp7)}</Button></div></div>{showDivider && <Divider />}</div></FormProvider> : <div className="flex flex-col gap-2"><div className={cx("group flex items-center justify-between rounded-default px-3 py-2", {
        "transition-colors hover:bg-hover": canEdit
      })}><div className="flex min-w-0 flex-col gap-1 break-words"><p className="text-label-2" style={{
            color: entry.hexCode
          }}>{entry.displayName}</p>{shouldShowSlug && <p className="text-body-2 text-tertiary">{entry.slug}</p>}</div>{canEdit && <IconButton icon={IcoPencil16} aria-label={t(_temp8)} className="shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" onClick={() => {
          onEdit();
        }} />}</div>{showDivider && <Divider />}</div>;
    $[12] = canEdit;
    $[13] = entry.displayName;
    $[14] = entry.hexCode;
    $[15] = entry.isEditing;
    $[16] = entry.slug;
    $[17] = form;
    $[18] = handleCancelEdit;
    $[19] = handleSave;
    $[20] = isNew;
    $[21] = onEdit;
    $[22] = shouldShowSlug;
    $[23] = showDivider;
    $[24] = t;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return t6;
};
export default CustomStatusEntryItem;
function _temp(t0) {
  const {
    field
  } = t0;
  return <ColorComponent value={field.value} onChange={field.onChange} />;
}
function _temp2($) {
  return $.displayNameRequired;
}
function _temp3($_0) {
  return $_0.displayNamePlaceholder;
}
function _temp4($_1) {
  return $_1.slugRequired;
}
function _temp5($_2) {
  return $_2.slugPlaceholder;
}
function _temp6($_3) {
  return $_3.cancel;
}
function _temp7($_4) {
  return $_4.save;
}
function _temp8($_5) {
  return $_5.edit;
}